import React, { useState } from 'react'

import { useHistory } from 'react-router-dom'
import { useParams } from 'utils/hooks'
import { useGroups } from 'redux/hooks/Groups'
import { useMetric } from 'redux/hooks/Metrics'
import { useTask } from 'redux/hooks/Tasks'
import { useTaskActions } from 'redux/actions/Tasks'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Text from 'components_new/atoms/Text'

import ContentBox from 'components_new/molecules/ContentBox'
import FormSection from 'components_new/molecules/FormSection'
import GroupsChip from 'components_new/molecules/GroupsChip'
import Hero from 'components_new/molecules/Hero'
import MetaRow from 'components_new/molecules/MetaRow'
import NavigationItem from 'components_new/molecules/NavigationItem'
import RichTextEditor from 'components_new/molecules/RichTextEditor'
import SearchAndSelectPopover from 'components_new/molecules/SearchAndSelectPopover'
import ValueText from 'components_new/molecules/ValueText'

import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'
import MetricView from 'components_new/organisms/MetricView'
import SelectUser from 'components_new/organisms/SelectUser'
import SubtasksTable from 'components_new/organisms/SubtasksTable'
import TaskDateField from 'components_new/organisms/tasks/TaskDateField'
import TaskStatusChip from 'components_new/organisms/tasks/TaskStatusChip'

import PageLayout from 'components_new/templates/PageLayout'

import { getPrefixValue } from 'components_new/organisms/Widget/Types/utils'
import { getAutoScale } from 'utils/autoFormat'
import { getParsedDate } from 'utils/dateParser'

import { Option } from 'components_new/molecules/SelectSearch'
import { TGroup } from 'types/GlobalGroup'
import { toEditorValue } from 'components_new/molecules/RichTextEditor/contract'

const Task = () => {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const task = useTask(id)

  const isSubtask = Boolean(task?.parent_task_id)

  const { t } = useTranslation()
  const { tryDeleteTask, tryUpdateTask } = useTaskActions()

  const [deleteOpen, setDeleteOpen] = useState(false)

  const metric = useMetric(
    task?.metric?.id,
    task?.metric?.settings.variant_kpi_template_id
  )

  const [editDescription, setEditDescription] = useState<boolean>(false)

  const { allGroups } = useGroups()

  if (!task || isSubtask) return null

  // Start/target drawn as reference lines on the metric graph (only when the
  // metric is shown).
  const metricReferenceLines: { value: number; label?: string }[] = []

  if (task.metric && task.start_value !== null) {
    metricReferenceLines.push({
      label: t('tasks', 'start_value'),
      value: task.start_value
    })
  }

  if (task.metric && task.target_value !== null) {
    metricReferenceLines.push({
      label: t('tasks', 'target_value'),
      value: task.target_value
    })
  }

  // start and target share one unit, e.g. "2,34 mkr → 0 mkr"
  const { unitPrefix, unitLabel } = getAutoScale(
    [task.start_value, task.target_value],
    metric?.unit,
    metric?.unit_prefixes
  )

  const handleUpdateUser = (value: string | null) => {
    tryUpdateTask(task.id, {
      responsible_account_id: value
    })
  }

  const groupOptions: Option[] = Object.values(allGroups).map((group) => ({
    avatar: 'group',
    color: group.profile_color,
    id: group.id,
    title: group.name
  }))

  const selectedGroups = task.group_ids
    .map((groupId) => allGroups.find((group) => group.id === groupId))
    .filter(Boolean) as TGroup[]

  return (
    <>
      <PageLayout
        topbar={{
          breadcrumbs: [
            {
              href: '/tasks',
              icon: { name: 'Tasks' },
              title: t('tasks', 'tasks')
            },
            { title: task.title || t('global', 'unnamed') }
          ],
          options: [
            {
              color: 'error',
              iconName: 'Delete',
              onClick: () => setDeleteOpen(true),
              title: t('global', 'delete')
            }
          ]
        }}
        variant="feed"
      >
        <Hero
          actions={
            <TaskStatusChip
              onChange={(status) => tryUpdateTask(task.id, { status })}
              value={task.status}
            />
          }
          disablePadding={true}
          editable={{
            onSubmit: (value) => {
              if (value) {
                tryUpdateTask(task.id, { title: value })
              }
            },
            placeholder: t('tasks', 'title_placeholder'),
            title: task.title
          }}
          metaItems={[
            {
              content: task.metric_filter_value,
              label: t('org_units', 'unit')
            },
            {
              content: (
                <SelectUser
                  onChange={handleUpdateUser}
                  size="small"
                  value={task.responsible_account_id}
                />
              ),
              label: t('global', 'owner')
            },
            {
              content: (
                <SearchAndSelectPopover
                  multiple={true}
                  onChange={(value) =>
                    tryUpdateTask(task.id, { group_ids: value as string[] })
                  }
                  options={groupOptions}
                  value={task.group_ids}
                >
                  <GroupsChip groups={selectedGroups} />
                </SearchAndSelectPopover>
              ),
              label: t('global', 'groups')
            },
            ...(task.analysis
              ? [
                  {
                    content: (
                      <NavigationItem
                        arrow={true}
                        border={true}
                        color="primary"
                        item={{
                          href: `/analyses/${task.analysis.id}`,
                          icon: { name: 'Analysis' },
                          title: task.analysis.title
                        }}
                      />
                    ),
                    label: t('analyses', 'analysis')
                  }
                ]
              : [])
          ]}
          size="medium"
          sx={{ mb: 2 }}
        />

        <MetaRow
          items={[
            {
              content: getParsedDate(task.created_at),
              label: t('global', 'created')
            },
            {
              content: (
                <TaskDateField
                  field="started_date"
                  onChange={(value) =>
                    tryUpdateTask(task.id, { started_date: value })
                  }
                  placeholder={t('tasks', 'not_started')}
                  task={task}
                />
              ),
              label: t('tasks', 'started')
            },
            {
              content: (
                <TaskDateField
                  field="completed_date"
                  onChange={(value) =>
                    tryUpdateTask(task.id, { completed_date: value })
                  }
                  placeholder={t('tasks', 'not_completed')}
                  task={task}
                />
              ),
              label: t('tasks', 'completed')
            }
          ]}
          sx={{ mb: 4 }}
        />

        <Box sx={{ mb: 2 }}>
          <FormSection
            actions={
              <IconButton
                onClick={() => setEditDescription(true)}
                size="small"
                sx={{ opacity: editDescription ? 0 : 1 }}
              >
                <Icon fontSize="small" name="Edit" />
              </IconButton>
            }
            size="small"
            title={t('global', 'description')}
          >
            <RichTextEditor
              autoFocus={true}
              edit={editDescription}
              editHeight={120}
              onCancel={() => setEditDescription(false)}
              onEdit={() => setEditDescription(true)}
              onSave={(value) => {
                tryUpdateTask(task.id, { description: value ?? null })
                setEditDescription(false)
              }}
              placeholder={t('tasks', 'write_description_placeholder')}
              settings={{
                bold: true,
                bulletedList: true,
                italic: true,
                numberedList: true
              }}
              value={toEditorValue(task.description)}
              variant="outlined"
            />
          </FormSection>
        </Box>

        <SubtasksTable
          items={task.sub_tasks}
          parentId={task.id}
          sx={{ mb: 4 }}
        />

        <FormSection
          size="small"
          title={t('results', 'progress')}
        ></FormSection>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            mb: 2
          }}
        >
          {[
            {
              content: <Text variant="h5">{task.metric_filter_value}</Text>,
              title: 'Urval'
            },
            {
              content: (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <ValueText
                    tooltip={t('tasks', 'start_value')}
                    unit={unitLabel}
                    value={getPrefixValue(task.start_value, unitPrefix)}
                    variant="h5"
                  />
                  <Text variant="h5">→</Text>
                  <ValueText
                    tooltip={t('tasks', 'target_value')}
                    unit={unitLabel}
                    value={getPrefixValue(task.target_value, unitPrefix)}
                    variant="h5"
                  />
                </Box>
              ),
              title: 'Förbättring'
            },
            {
              content: (
                <ValueText
                  color="success.text"
                  displayPositiveIndicator={true}
                  unit={'kr/år'}
                  value={task.noi_change}
                  variant="h5"
                />
              ),
              title: 'Potentiellt resultat'
            }
          ].map((item, i) => (
            <ContentBox
              border={true}
              header={{
                title: item.title
              }}
              key={i}
              sx={{ flex: 1 }}
            >
              {item.content}
            </ContentBox>
          ))}
        </Box>

        {task.metric?.id ? (
          <ContentBox border={true}>
            <MetricView
              chart="line"
              clickOptions={{
                explore: true
              }}
              daily={true}
              id={task.metric.id}
              queryParams={{
                ...(task.metric_filter_value && task.metric_filter_attribute_id
                  ? {
                      filter: {
                        [task.metric_filter_attribute_id]: {
                          eq: [task.metric_filter_value]
                        }
                      }
                    }
                  : null),
                line_1: metricReferenceLines[0]?.value,
                line_1_label: metricReferenceLines[0]?.label,
                line_2: metricReferenceLines[1]?.value,
                line_2_label: metricReferenceLines[1]?.label
              }}
              setQueryParams={() => {}}
              settings={task.metric.settings}
              size="small"
              subTasks={task.sub_tasks}
              tasks={[task]}
              TitleProps={{
                link: true
              }}
              sx={{ height: 400 }}
            />
          </ContentBox>
        ) : null}
      </PageLayout>

      {/*-- dialogs --*/}
      <DeleteDialog
        onClose={() => setDeleteOpen(false)}
        onDelete={(cb) => {
          tryDeleteTask(task.id, (err) => {
            if (!err) {
              setDeleteOpen(false)
              history.push('/tasks')
            }
            cb()
          })
        }}
        open={deleteOpen}
        title={task.title || t('global', 'unnamed')}
        type={t('tasks', 'task', true)}
      />
    </>
  )
}

export default Task
